// Source unique de vérité pour Account.status.
//
// Ces valeurs sont persistées en BDD (colonne `status` de Account, un String
// libre côté Prisma) : toute valeur hors de cette liste devient un statut
// orphelin, invisible des filtres de widgets/PFM qui raisonnent en termes de
// "challenge" / "pa" / "live" / "demo" / "inactive".
//
// Le bug historique : l'écran d'import Tradovate proposait "funded" (un
// phaseType de prop firm, PAS un Account.status) en plus de "pa". Les comptes
// importés avec ce statut disparaissaient du PropFirmManager et des pages
// personnalisées filtrées par statut, alors que "funded" et "pa" désignent la
// même chose côté produit.
export const ACCOUNT_STATUSES = ["challenge", "pa", "inactive", "demo", "live"] as const;

export type AccountStatus = (typeof ACCOUNT_STATUSES)[number];

/**
 * Normalise un statut venant d'une source externe (import Tradovate, ancien
 * client, payload legacy) vers la valeur canonique.
 *
 * "funded" est un alias historique de "pa" (Performance Account) : on le
 * remappe au lieu de le rejeter pour ne pas casser les imports en cours.
 */
export function normalizeAccountStatus(
  raw: string | null | undefined,
  fallback: AccountStatus = "live",
): AccountStatus {
  if (!raw) return fallback;
  const value = raw.trim().toLowerCase();
  if (value === "funded") return "pa";
  return (ACCOUNT_STATUSES as readonly string[]).includes(value)
    ? (value as AccountStatus)
    : fallback;
}

/**
 * Le compte est-il FINANCÉ par une prop firm (règles funded/live, payouts) ?
 *
 * "pa" l'est toujours. "live" est AMBIGU : c'est à la fois le statut d'un
 * compte perso (sans prop firm, aucune règle) et celui d'un compte promu sur le
 * programme live de sa prop firm (argent réel, phase `live` du palier). Seul le
 * rattachement à une prop firm les distingue : sans lui, traiter un compte perso
 * comme financé ferait apparaître payouts et compteurs prop firm à tort.
 *
 * Un compte live naît live (nouveau compte ouvert par la prop firm sur l'hôte
 * live) : il n'a jamais de `fundedAt`, comme un direct funded.
 */
export function isPropFirmFundedStatus(
  status: string | null | undefined,
  hasPropFirm: boolean,
): boolean {
  if (status === "pa") return true;
  return status === "live" && hasPropFirm;
}
